import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WorkflowData } from "../WorkflowEditor";
import { Button } from "@/components/ui/button";
import { Wand2 } from "lucide-react";

interface BasicInfoTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function BasicInfoTab({ data, onChange }: BasicInfoTabProps) {
  const categories = [
    { value: "member_operations", label: "Member Operations" },
    { value: "asset_management", label: "Asset Management" },
    { value: "governance", label: "Governance & Compliance" },
    { value: "financial_operations", label: "Financial Operations" },
    { value: "estate_planning", label: "Estate Planning" },
  ];

  const difficulties = [
    { value: "beginner", label: "Beginner" },
    { value: "intermediate", label: "Intermediate" },
    { value: "advanced", label: "Advanced" },
  ];

  const icons = [
    "user-plus", "users", "briefcase", "home", "building",
    "dollar-sign", "file-text", "shield", "clipboard", "award"
  ];

  function generateKey() {
    const key = data.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    onChange({ ...data, workflow_key: key });
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="workflow_key">Workflow Key *</Label>
            <div className="flex gap-2">
              <Input
                id="workflow_key"
                value={data.workflow_key}
                onChange={(e) => onChange({ ...data, workflow_key: e.target.value })}
                placeholder="add-member"
              />
              <Button variant="outline" size="sm" onClick={generateKey}>
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Unique identifier (lowercase, hyphens only)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={data.category} onValueChange={(value) => onChange({ ...data, category: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={data.title}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="Add a New Member"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={data.description}
            onChange={(e) => onChange({ ...data, description: e.target.value })}
            placeholder="Complete workflow for adding a new member to your UNA, including all required documentation and signatures."
            rows={4}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="icon">Icon</Label>
            <Select value={data.icon_name} onValueChange={(value) => onChange({ ...data, icon_name: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {icons.map(icon => (
                  <SelectItem key={icon} value={icon}>
                    {icon}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated_time">Estimated Time</Label>
            <Input
              id="estimated_time"
              value={data.estimated_time}
              onChange={(e) => onChange({ ...data, estimated_time: e.target.value })}
              placeholder="15-20 minutes"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="difficulty">Difficulty</Label>
            <Select value={data.difficulty} onValueChange={(value) => onChange({ ...data, difficulty: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {difficulties.map(diff => (
                  <SelectItem key={diff.value} value={diff.value}>
                    {diff.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tier Restrictions</Label>
          <div className="space-y-2">
            {['foundation', 'active_path', 'elite'].map(tier => (
              <label key={tier} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.tier_restrictions.includes(tier)}
                  onChange={(e) => {
                    const newTiers = e.target.checked
                      ? [...data.tier_restrictions, tier]
                      : data.tier_restrictions.filter(t => t !== tier);
                    onChange({ ...data, tier_restrictions: newTiers });
                  }}
                  className="rounded"
                />
                <span className="capitalize">{tier.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Leave empty to allow all tiers
          </p>
        </div>
      </div>
    </div>
  );
}